/**
 * Project Modules — UI placeholder (Project Setup → Project Modules).
 *
 * Which modules a project uses is not modelled anywhere yet: nothing persists
 * these toggles and nothing reads them, so the sidebar is unaffected. The
 * toggles are local state purely so the layout can be reviewed.
 *
 * When this is implemented, the enabled set belongs on the project (a JSONB
 * column or a project_modules table) and the sidebar in Navigation.tsx has to
 * read it, otherwise a "disabled" module stays reachable by URL.
 */
import {useMemo, useState} from 'react';
import {
  Users,
  UserSquare2,
  CheckSquare,
  CalendarClock,
  CalendarDays,
  Upload,
  DollarSign,
  Mail,
  MessageSquare,
  Flag,
  type LucideIcon,
} from 'lucide-react';

import {Badge} from '@/shared/ui/badge';
import {Button} from '@/shared/ui/button';
import {Switch} from '@/shared/ui/switch';
import {cn} from '@/lib/utils';

interface ModuleDef {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Mandatory modules are always on and cannot be switched off. */
  mandatory?: boolean;
  defaultEnabled?: boolean;
}

const MODULES: ModuleDef[] = [
  {key: 'team', label: 'Team', description: 'Roles, ownership, member management', icon: Users, mandatory: true},
  {key: 'participants', label: 'Participants', description: 'Roster, eligibility, consent, quota', icon: UserSquare2, mandatory: true},
  {key: 'tasks', label: 'Tasks', description: 'Task views & data collection', icon: CheckSquare, defaultEnabled: true},
  {key: 'scheduler', label: 'Scheduler', description: 'Sessions, slots, invites', icon: CalendarClock, mandatory: true},
  {key: 'schedule', label: 'Schedule', description: 'Booked sessions & calendar', icon: CalendarDays, mandatory: true},
  {key: 'data_uploads', label: 'Data Uploads', description: 'Files & metadata validation', icon: Upload, defaultEnabled: true},
  {key: 'payment_approvals', label: 'Payment Approvals', description: 'Bonuses & partial payments', icon: DollarSign, defaultEnabled: true},
  {key: 'email_setup', label: 'Email Setup', description: 'Participant email templates', icon: Mail, defaultEnabled: true},
  {key: 'sms_setup', label: 'SMS Setup', description: 'Participant SMS templates', icon: MessageSquare, defaultEnabled: false},
  {key: 'action_center', label: 'Action Center', description: 'Flags & pending approvals', icon: Flag, defaultEnabled: true},
];

const MANDATORY_LABELS = MODULES.filter((m) => m.mandatory).map((m) => m.label);

export const ProjectModulesPanel = () => {
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      MODULES.map((m) => [m.key, Boolean(m.mandatory || m.defaultEnabled)]),
    ),
  );

  const enabledCount = useMemo(
    () => MODULES.filter((m) => enabled[m.key]).length,
    [enabled],
  );

  const toggle = (key: string) =>
    setEnabled((prev) => ({...prev, [key]: !prev[key]}));

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-lg border border-dashed border-slate-300 bg-muted/40 p-3 dark:border-slate-600">
        <Badge variant="secondary" className="mt-0.5 shrink-0 uppercase tracking-wide">
          Preview
        </Badge>
        <p className="text-sm text-muted-foreground">
          Layout preview only — these toggles are not saved and do not change the
          project sidebar yet. Every module remains available regardless of what is
          switched off here.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Selected Modules</h2>
          <p className="text-sm text-muted-foreground">
            Choose which modules this project uses.
          </p>
        </div>
        <Button disabled title="Module selection cannot be saved yet">
          Finalize setup
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {MODULES.map((module) => {
          const Icon = module.icon;
          const isOn = Boolean(enabled[module.key]);
          return (
            <div
              key={module.key}
              className={cn(
                'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                isOn
                  ? 'border-slate-200 dark:border-slate-700'
                  : 'border-slate-200 bg-muted/40 opacity-70 dark:border-slate-700',
              )}
            >
              <span
                aria-hidden="true"
                className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
              >
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">{module.label}</span>
                  {module.mandatory && (
                    <Badge variant="outline" className="shrink-0 text-[10px] uppercase">
                      Required
                    </Badge>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {module.description}
                </p>
              </div>
              <Switch
                checked={isOn}
                disabled={module.mandatory}
                onCheckedChange={() => toggle(module.key)}
                aria-label={`${isOn ? 'Disable' : 'Enable'} ${module.label}`}
              />
            </div>
          );
        })}
      </div>

      <p className="text-sm text-muted-foreground">
        {enabledCount} of {MODULES.length} modules enabled — disabled modules would be
        hidden from the project sidebar for all roles. {MANDATORY_LABELS.join(', ')} are
        mandatory and always on.
      </p>
    </div>
  );
};

export default ProjectModulesPanel;
